import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function youTubeVideoId(url) {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?(?:.*&)?v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/embed\/([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
    /youtube\.com\/live\/([\w-]{11})/,
    /youtube\.com\/v\/([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function youTubeEmbedUrl(url) {
  const id = youTubeVideoId(url);
  // youtube-nocookie + an explicit origin avoids the player's
  // "Video player configuration error" when the referrer is stripped.
  if (!id) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const params = new URLSearchParams({ rel: '0', modestbranding: '1', playsinline: '1' });
  if (origin) params.set('origin', origin);
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

function DashboardItem({ item }) {
  const embed = item.type === 'video' ? youTubeEmbedUrl(item.url) : null;
  const thumbStyle = item.thumbnailWidth ? { width: `${item.thumbnailWidth}px`, maxWidth: '100%' } : undefined;
  const defaultLinkText = item.type === 'article' ? 'Read more →' : 'Open link →';
  const linkText = (item.linkText || '').trim() || defaultLinkText;

  return (
    <div className="dash-item">
      {item.type === 'video' && embed ? (
        <div className="dash-video">
          <iframe
            src={embed}
            title={item.title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ) : (
        item.thumbnailUrl && (
          <div className="dash-thumb">
            {item.url ? (
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                <img src={item.thumbnailUrl} alt={item.title} style={thumbStyle} />
              </a>
            ) : (
              <img src={item.thumbnailUrl} alt={item.title} style={thumbStyle} />
            )}
          </div>
        )
      )}
      <div className="dash-item-body">
        <h3>{item.title}</h3>
        {item.body && <p>{item.body}</p>}
        {item.type === 'video' && !embed && item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="dash-link">
            Watch on YouTube →
          </a>
        )}
        {item.type !== 'video' && item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="dash-link">
            {linkText}
          </a>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const res = await fetch('/api/dashboard', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSections(data.sections || []);
          setItems(data.items || []);
        }
      } catch {
        // ignore — dashboard content is optional
      } finally {
        setLoading(false);
      }
    }
    fetchDashboard();
  }, []);

  const sectionedIds = new Set();
  const sectionContent = sections.map((section) => {
    const secItems = items.filter((it) => it.sectionId === section.id);
    secItems.forEach((it) => sectionedIds.add(it.id));
    return { section, items: secItems };
  }).filter((s) => s.items.length > 0);
  const unsectioned = items.filter((it) => !sectionedIds.has(it.id));

  // Anchor targets for the jump navigation
  const navTargets = [
    ...sectionContent.map(({ section }) => ({ id: `sec-${section.id}`, name: section.name })),
    ...(unsectioned.length > 0 ? [{ id: 'sec-updates', name: 'News & Updates' }] : []),
  ];

  // Highlight the section currently in view
  useEffect(() => {
    if (navTargets.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    navTargets.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items.length, sections.length]);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setNavOpen(false);
  };

  const sectionNav = navTargets.length > 0 && (
    <nav className="dash-nav" aria-label="Dashboard sections">
      <ul>
        {navTargets.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              className={`dash-nav-link ${activeSection === t.id ? 'active' : ''}`}
              onClick={() => jumpTo(t.id)}
            >
              {t.name}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );

  const profileCard = (
    <div className="profile-card">
      <div className="profile-avatar">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt={user.name} />
        ) : (
          <div className="avatar-placeholder">
            {user.name ? user.name.charAt(0).toUpperCase() : '?'}
          </div>
        )}
      </div>
      <div className="profile-info">
        <h2>{user.name}</h2>
        <p className="profile-email">{user.email}</p>
        <span className={`badge badge-${user.tier}`}>
          {user.tier ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1) : 'No Tier'}
        </span>
        {user.role === 'admin' && (
          <span className="badge badge-admin">Admin</span>
        )}
      </div>
      <Link to="/shop" className="btn btn-primary btn-full">Go to Shop →</Link>
    </div>
  );

  return (
    <div className="page dashboard-page">
      {/* Mobile-only: user card and collapsible section jump list */}
      <div className="dash-mobile-top">
        {profileCard}
        {navTargets.length > 0 && (
          <>
            <button
              type="button"
              className="dash-nav-toggle"
              onClick={() => setNavOpen((o) => !o)}
              aria-expanded={navOpen}
            >
              <span className="hamburger" aria-hidden="true" />
              Jump to section
            </button>
            {navOpen && <div className="dash-nav-drawer">{sectionNav}</div>}
          </>
        )}
      </div>

      <div className="dashboard-layout">
        <aside className="dash-nav-col">
          {navTargets.length > 0 && (
            <div className="dash-nav-sticky">
              <h2 className="dash-nav-title">Sections</h2>
              {sectionNav}
            </div>
          )}
        </aside>

        <main className="dash-main">
          <div className="dashboard-hero">
            <h1>Dashboard</h1>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <>
              {sectionContent.map(({ section, items: secItems }) => (
                <div key={section.id} id={`sec-${section.id}`} className="dash-section">
                  <h2 className="dash-section-title">{section.name}</h2>
                  <div className="dash-grid">
                    {secItems.map((it) => <DashboardItem key={it.id} item={it} />)}
                  </div>
                </div>
              ))}
              {unsectioned.length > 0 && (
                <div id="sec-updates" className="dash-section">
                  {sectionContent.length > 0 && <h2 className="dash-section-title">News &amp; Updates</h2>}
                  <div className="dash-grid">
                    {unsectioned.map((it) => <DashboardItem key={it.id} item={it} />)}
                  </div>
                </div>
              )}
              {sectionContent.length === 0 && unsectioned.length === 0 && (
                <p className="empty-state">
                  No news or updates yet.
                  {isAdmin && <> Head to <Link to="/admin/dashboard">Manage Dashboard</Link> to add content.</>}
                </p>
              )}
            </>
          )}
        </main>

        <aside className="dash-side-col">
          <div className="dash-side-sticky">{profileCard}</div>
        </aside>
      </div>
    </div>
  );
}
